import React from 'react'
import zionDebug from '@fs/zion-debug'
import { useFeatureFlags } from '@fs/zion-flags'
import { getImage, useSlsStateContext } from '@fs/zion-sls'
import { Button, Divider, Image, List, Paragraph, Row, Separator, Skeleton } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useUser } from '@fs/zion-user'
import { baseUrl } from '@fs/zion-config'
import { useSharedBrowserStateContext } from '../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { useR2StateContext, useR2DispatchContext } from '../../../../providers/r2Provider/R2Context'
import useInfoSheetOrientationCachedState from '../../../../../shared/hooks/useInfoSheetOrientationCachedState'
import useIsHeaderHidden from '../../../../../shared/hooks/useIsHeaderHidden'
import searchRecordImg from '../../../../images/search-record.png'
import useRecords from '../../hooks/useRecords'
import ShowChangeHistory from '../ui/ShowChangeHistory'
import OtherPersonsMentioned from './OtherPersonsMentioned'
import PersonDisplay from './PersonDisplay'
import IndexedDataHeader, { NamesNotInRecordsHeader } from './SelectPersonHeaders'

const debug = zionDebug('rip:R2:SelectPerson') // localStorage.debug = "fs:rip:R2:SelectPerson"

export default function SelectPerson({ loading = false }) {
  const [t] = useTranslation()
  const { highlightedPersonId } = useR2StateContext()
  const { isBottomSheet } = useInfoSheetOrientationCachedState()
  const isHeaderHidden = useIsHeaderHidden()

  const { rip_sharedImageViewer_disabledPreviewNextImage: disableRelatedImageLoad } = useFeatureFlags([
    'rip_sharedImageViewer_disabledPreviewNextImage',
  ])
  const disableRelatedImageLoadOn = disableRelatedImageLoad.isOn
  const zionSlsState = useSlsStateContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const r2Dispatch = useR2DispatchContext()
  const { currentImageArk } = zionSlsState
  const personIdsOrderForImage = zionSlsState.personOrder?.find((po) => po.partitionKey === currentImageArk)
  const currentImage = getImage(zionSlsState.images, zionSlsState?.currentImageArk)

  // const hasOnlyFloatingData
  const { allRecords, floatingPersons, noPeople, imageHasData, isJiapu } = useRecords({
    images: zionSlsState?.images,
    currentImageArk: zionSlsState?.currentImageArk,
    personIdsOrder: personIdsOrderForImage?.personIds,
    thumbnails,
  })
  const globalLoading = loading || zionSlsState.loading
  const showLoading = loading || (noPeople && zionSlsState.loading)
  const handlePersonSelect = (personId) => {
    r2Dispatch({ type: 'SET_SELECTED_PERSON_ID', payload: personId }) // Dispatch the selected person ID to R2
  }
  debug({ allRecords, floatingPersons, noPeople, imageHasData })

  const { signedIn } = useUser()

  const noDataMessage = !signedIn
    ? t('select-person.subheading.need-to-login', 'Sign in to view indexing information.')
    : t('select-person.subheading.not-indexed', 'This image has not been indexed.')

  const signInUrl = `${baseUrl}/auth/familysearch/login?returnUrl=${encodeURIComponent(window.location)}`
  const signUpUrl = `${baseUrl}/auth/familysearch/login?option=createaccount&returnUrl=${encodeURIComponent(window.location)}`

  const hasRecords = currentImage?.subElements?.length > 0

  debug('SelectPerson: ', { floatingPersons, hasRecords, allRecords, signedIn, isJiapu, globalLoading })

  return (
    <>
      <IndexedDataHeader />
      <Separator size="xs" />
      {!isJiapu &&
        allRecords?.map((record, i) => {
          const persons = record?.subElements?.filter(
            (sub) => sub?.elementType === 'PERSON' && sub?.partitionKey === currentImageArk
          )

          return (
            <div key={record?.id}>
              {persons && persons.length > 0 && i !== 0 && (
                <>
                  <Separator size="xs" />
                  <Divider fullWidth />
                  <Separator size="xs" />
                </>
              )}
              {persons && persons.length > 0 && (
                <List>
                  {persons.map((person) => {
                    return (
                      <PersonDisplay
                        person={person}
                        t={t}
                        key={person?.id}
                        record={record}
                        isSelected={highlightedPersonId === person?.id}
                        onPersonSelect={handlePersonSelect}
                      />
                    )
                  })}
                </List>
              )}
              <OtherPersonsMentioned record={record} disableRelatedImageLoadOn={disableRelatedImageLoadOn} />
            </div>
          )
        })}
      {floatingPersons && !isJiapu && floatingPersons?.length > 0 && (
        <div>
          <Separator size="xs" />
          <Divider fullWidth />
          <NamesNotInRecordsHeader />
          {floatingPersons
            .sort((a, b) => (a.primary > b.primary ? -1 : 1))
            .map((person) => {
              return (
                <PersonDisplay
                  person={person}
                  zionSlsState={zionSlsState}
                  t={t}
                  key={person?.id}
                  isSelected={highlightedPersonId === person?.id}
                  onPersonSelect={handlePersonSelect}
                />
              )
            })}
        </div>
      )}
      {!globalLoading && (noPeople || !imageHasData || isJiapu) && (
        <>
          <Image src={searchRecordImg} alt="" />
          <Paragraph size="sm" centered>
            {noDataMessage}
          </Paragraph>
          {!signedIn && isHeaderHidden && (
            <>
              <Separator size="xs" />
              {isBottomSheet ? (
                <Row alignX="center" spacing="sm">
                  <Button
                    emphasis="high"
                    external
                    to={signInUrl}
                    data-testid="sign-in-button"
                    linkName="Sign In from Select Person"
                  >
                    {t('common-ui:sign-in.action', 'Sign In')}
                  </Button>
                  <Button
                    emphasis="medium"
                    external
                    to={signUpUrl}
                    data-testid="sign-up-button"
                    linkName="Sign Up from Select Person"
                  >
                    {t('select-person.sign-up.action', 'Sign Up')}
                  </Button>
                </Row>
              ) : (
                <>
                  <Button
                    emphasis="high"
                    fullWidth
                    external
                    to={signInUrl}
                    data-testid="sign-in-button"
                    linkName="Sign In from Select Person"
                  >
                    {t('common-ui:sign-in.action', 'Sign In')}
                  </Button>
                  <Separator size="xxs" />
                  <Button
                    emphasis="medium"
                    fullWidth
                    external
                    to={signUpUrl}
                    data-testid="sign-up-button"
                    linkName="Sign Up from Select Person"
                  >
                    {t('select-person.sign-up.action', 'Sign Up')}
                  </Button>
                </>
              )}
            </>
          )}
        </>
      )}
      {showLoading && (
        <List>
          <Skeleton.ListItem hasSecondLine />
          <Skeleton.ListItem hasSecondLine />
          <Skeleton.ListItem hasSecondLine />
          <Skeleton.ListItem hasSecondLine />
          <Skeleton.ListItem hasSecondLine />
          <Skeleton.ListItem hasSecondLine />
          <Skeleton.ListItem hasSecondLine />
          <Skeleton.ListItem hasSecondLine />
          <Skeleton.ListItem hasSecondLine />
          <Skeleton.ListItem hasSecondLine />
          <Skeleton.ListItem hasSecondLine />
          <Skeleton.ListItem hasSecondLine />
        </List>
      )}
      {!globalLoading && imageHasData && hasRecords && !isJiapu && <ShowChangeHistory />}
    </>
  )
}
